import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormArray,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';

import { ButtonModule } from '../../shared/button/button.module';
import { ControlMessagesComponent } from '../../shared/control-messages/control-messages.component';
import { BugReportDrawerService } from '../../../services/bug-report/bug-report-drawer.service';
import {
    BugReportPayload,
    BugReportService,
    BugScope,
    BugSeverity,
} from '../../../services/bug-report/bug-report.service';
import { BreadcrumbBufferService } from '../../../services/bug-report/breadcrumb-buffer.service';
import { FailedRequestBufferService } from '../../shared/http/failed-request-buffer.service';
import { GrowlerService } from '../../shared/growler/growler.service';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';

/** Typed reactive form backing the bug-report drawer. */
interface BugReportForm {
    summary: FormControl<string>;
    expected: FormControl<string>;
    actual: FormControl<string>;
    reproSteps: FormArray<FormControl<string>>;
    severity: FormControl<BugSeverity>;
    scope: FormControl<BugScope>;
}

/**
 * The "Report a bug" drawer — the ONE SIMPLE FORM surface. Mounted once in
 * `app.component.html` beside the Gable drawer host, behind the same
 * `*ngIf="isLoggedIn"`. Standalone + OnPush; the open/close state comes from
 * `BugReportDrawerService.isOpen$` (each emission calls `markForCheck`).
 *
 * Layout (top → bottom):
 *   - green "✓ We captured the tech details for you" trust panel, with a
 *     'view' expander showing the captured context;
 *   - four plain-language fields (summary / expected / actual / repro steps,
 *     repro steps prefilled from the breadcrumb buffer and editable);
 *   - "How bad?" severity toggle and "Who's affected?" scope toggle;
 *   - "Preview what we send" expander showing the assembled redacted payload;
 *   - "Send bug report" primary button.
 */
@UntilDestroy()
@Component({
    selector: 'app-report-bug-drawer-host',
    templateUrl: './report-bug-drawer-host.component.html',
    styleUrls: ['./report-bug-drawer-host.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        ControlMessagesComponent,
        MatButtonToggleModule,
        MatFormFieldModule,
        MatInputModule,
    ],
})
export class ReportBugDrawerHostComponent implements OnInit {
    isOpen = false;
    submitting = false;

    /** Trust-panel context expander. */
    showCaptured = false;
    /** Redacted-payload preview expander. */
    showPreview = false;
    /** Pre-computed redacted-payload JSON shown while the preview is open.
     *  Recomputed on form changes (debounced) rather than in a template getter. */
    previewJson = '';

    form!: FormGroup<BugReportForm>;

    /** Severity options for the "How bad?" toggle. */
    readonly severities: ReadonlyArray<{ value: BugSeverity; label: string }> = [
        { value: 'blocking', label: 'Blocking' },
        { value: 'major', label: 'Major' },
        { value: 'minor', label: 'Minor' },
    ];

    /** Scope options for the "Who's affected?" toggle. */
    readonly scopes: ReadonlyArray<{ value: BugScope; label: string }> = [
        { value: 'single_user', label: '1 user' },
        { value: 'single_account', label: '1 org' },
        { value: 'widespread', label: 'Many' },
    ];

    constructor(
        private _drawer: BugReportDrawerService,
        private _service: BugReportService,
        private _breadcrumbs: BreadcrumbBufferService,
        private _failedRequests: FailedRequestBufferService,
        private _growler: GrowlerService,
        private _cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this._buildForm();
        this._drawer.isOpen$.pipe(untilDestroyed(this)).subscribe((open) => {
            this.isOpen = open;
            // Refresh the prefilled repro steps each time the drawer opens so it
            // reflects the breadcrumbs captured right up to the moment of filing.
            if (open) {
                this._resetReproStepsFromBreadcrumbs();
            }
            this._cdr.markForCheck();
        });

        // Keep the preview JSON fresh off the form's value stream (debounced)
        // instead of recomputing the redacted payload in a template getter every
        // change-detection cycle. Only does work while the preview is open.
        this.form.valueChanges.pipe(debounceTime(150), untilDestroyed(this)).subscribe(() => {
            if (this.showPreview) {
                this._refreshPreview();
            }
        });
    }

    private _buildForm(): void {
        this.form = new FormGroup<BugReportForm>({
            summary: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
            // Required to match the server contract (core CreateBugReportRequest
            // requires expected + actual) — fail fast in the client, not at 422.
            expected: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
            actual: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
            reproSteps: new FormArray<FormControl<string>>([]),
            severity: new FormControl<BugSeverity>('major', { nonNullable: true }),
            scope: new FormControl<BugScope>('single_account', { nonNullable: true }),
        });
    }

    private _resetReproStepsFromBreadcrumbs(): void {
        const steps = this._breadcrumbs.getSteps();
        const array = this.form.controls.reproSteps;
        array.clear();
        if (steps.length === 0) {
            array.push(new FormControl('', { nonNullable: true }));
            return;
        }
        for (const step of steps) {
            array.push(new FormControl(step, { nonNullable: true }));
        }
    }

    get reproSteps(): FormArray<FormControl<string>> {
        return this.form.controls.reproSteps;
    }

    addReproStep(): void {
        this.reproSteps.push(new FormControl('', { nonNullable: true }));
        this._cdr.markForCheck();
    }

    removeReproStep(index: number): void {
        if (this.reproSteps.length > 1) {
            this.reproSteps.removeAt(index);
            this._cdr.markForCheck();
        }
    }

    trackByIndex(index: number): number {
        return index;
    }

    /**
     * Track repro-step rows by their FormControl instance, NOT by index.
     * Index tracking + removeAt() makes Angular re-map rows by position, so
     * removing row N visually drops the LAST row. Tracking by the control keeps
     * each row bound to its own control, so the correct row is removed.
     */
    trackByControl(_: number, control: FormControl<string>): FormControl<string> {
        return control;
    }

    // -------------------------------------------------------------------------
    // Trust panel "captured" summary
    // -------------------------------------------------------------------------

    get capturedPage(): string {
        return window.location.pathname;
    }

    get capturedRecentClicks(): number {
        return this._breadcrumbs.getBreadcrumbs().filter((b) => b.type === 'click').length;
    }

    get capturedErrors(): number {
        return this._failedRequests.getAll().length;
    }

    get capturedTraceId(): string {
        const failed = this._failedRequests.getAll();
        const newest = failed.length > 0 ? failed[failed.length - 1].traceId : null;
        return newest ?? this._failedRequests.getLastTraceId() ?? 'none';
    }

    /** True when a real trace id is available (i.e. not the 'none' placeholder). */
    get capturedHasTrace(): boolean {
        return this.capturedTraceId !== 'none';
    }

    /** Short "org · role" label from the active JWT, for the captured-context chips. */
    get capturedAccount(): string {
        const jwt = LegFiJwtService.read();
        const role = jwt?.admin ? 'admin' : (jwt?.superUser ? 'super' : 'member');
        return `${jwt?.orgName ?? 'org'} · ${role}`;
    }

    /** Short, human-friendly browser label derived from the user agent. */
    get capturedBrowser(): string {
        const ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
        if (!ua) {
            return 'browser';
        }

        let name = 'browser';
        if (/Edg\//.test(ua)) {
            name = 'Edge';
        } else if (/Firefox\//.test(ua)) {
            name = 'Firefox';
        } else if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) {
            name = 'Chrome';
        } else if (/Safari\//.test(ua) && /Version\//.test(ua)) {
            name = 'Safari';
        }

        let os = '';
        if (/Macintosh|Mac OS X/.test(ua)) {
            os = ' on Mac';
        } else if (/Windows/.test(ua)) {
            os = ' on Windows';
        }

        return `${name}${os}`;
    }

    /** Recent breadcrumb labels for the "view" expander. */
    get capturedSteps(): string[] {
        return this._breadcrumbs.getSteps();
    }

    toggleCaptured(): void {
        this.showCaptured = !this.showCaptured;
        this._cdr.markForCheck();
    }

    // -------------------------------------------------------------------------
    // Preview
    // -------------------------------------------------------------------------

    togglePreview(): void {
        this.showPreview = !this.showPreview;
        // Compute once on open (the valueChanges stream keeps it fresh after).
        if (this.showPreview) {
            this._refreshPreview();
        }
        this._cdr.markForCheck();
    }

    /** Assemble + redact the payload and stash its JSON for the preview pane. */
    private _refreshPreview(): void {
        this.previewJson = JSON.stringify(this._buildPayload(), null, 2);
    }

    private _buildPayload(): BugReportPayload {
        const value = this.form.getRawValue();
        return this._service.assemblePayload({
            summary: value.summary,
            expected: value.expected,
            actual: value.actual,
            reproSteps: value.reproSteps.filter((s) => s.trim().length > 0),
            severity: value.severity,
            scope: value.scope,
        });
    }

    // -------------------------------------------------------------------------
    // Submit / close
    // -------------------------------------------------------------------------

    onClose(): void {
        this._drawer.close();
    }

    onSend(): void {
        if (this.submitting || this.form.invalid) {
            this.form.markAllAsTouched();
            this._cdr.markForCheck();
            return;
        }
        this.submitting = true;
        this._cdr.markForCheck();

        const payload = this._buildPayload();
        this._service.submit(payload).pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.submitting = false;
                this._growler.success(
                    'Bug report submitted',
                    'Thanks — we captured the technical details for you.',
                );
                this._breadcrumbs.clear();
                this.form.reset({ severity: 'major', scope: 'single_account' });
                this._drawer.close();
                this._cdr.markForCheck();
            },
            error: () => {
                this.submitting = false;
                this._growler.error(
                    'Bug report failed',
                    'Sorry — we could not submit that bug report. Please try again in a moment.',
                );
                this._cdr.markForCheck();
            },
        });
    }
}
